import axios from '../axios';
import type { CommerceFormPrefill } from '../utils/commercePrefill.ts';

export type CommerceDeliveryScope = 'LOCAL' | 'NATIONWIDE' | 'LISTED_ZONES';

export const COMMERCE_DELIVERY_RADIUS_KM_MIN = 1;
export const COMMERCE_DELIVERY_RADIUS_KM_MAX = 5000;
export const COMMERCE_DELIVERY_RADIUS_UI_KM_MAX = 2000;
export const COMMERCE_LISTED_ZONES_ID_MIN = 1;
export const COMMERCE_LISTED_ZONES_ID_MAX = 200;
export const COMMERCE_LISTED_ZONES_MAX_JURISDICTIONS = 50;

export type ListedZonesByJurisdictionBucketDto = {
  jurisdiccionId: number;
  georefZonaIds: string[];
};

export interface CommerceDepartmentCity {
  id: string;
  name: string;
}

export interface CommerceDeliveryGeo {
  deliveryScope: CommerceDeliveryScope;
  latitude: number | null;
  longitude: number | null;
  deliveryRadiusKm: number | null;
  geocodeQuerySnapshot: string;
  geocodePlaceId: string | null;
  listedGeorefZonaIds?: string[] | null;
  listedZonesByJurisdiction?: ListedZonesByJurisdictionBucketDto[] | null;
}

export interface CreateCommercePayload {
  name: string;
  description: string;
  zone: string;
  delivery: boolean;
  delivery_condition?: string;
  url_site_web?: string;
  jurisdiccionId: number;
  userId: number;
  logo?: File | null;
  deliveryScope?: CommerceDeliveryScope;
  latitude?: number;
  longitude?: number;
  deliveryRadiusKm?: number;
  listedGeorefZonaIds?: string[];
  listedZonesByJurisdiction?: ListedZonesByJurisdictionBucketDto[];
}

function buildCommerceMultipart(payload: CreateCommercePayload): FormData {
  const formData = new FormData();
  formData.append('name', payload.name.trim());
  formData.append('description', payload.description.trim());
  formData.append('zone', payload.zone.trim());
  formData.append('delivery', payload.delivery ? 'true' : 'false');
  if (payload.delivery_condition?.trim()) {
    formData.append('delivery_condition', payload.delivery_condition.trim());
  }
  if (payload.url_site_web?.trim()) {
    formData.append('url_site_web', payload.url_site_web.trim());
  }
  formData.append('jurisdiccionId', String(payload.jurisdiccionId));
  formData.append('userId', String(payload.userId));
  if (payload.logo) {
    formData.append('logo', payload.logo);
  }
  if (payload.deliveryScope) {
    formData.append('deliveryScope', payload.deliveryScope);
  }
  if (payload.deliveryScope === 'LOCAL') {
    const kmRaw =
      payload.deliveryRadiusKm != null && Number.isFinite(payload.deliveryRadiusKm)
        ? payload.deliveryRadiusKm
        : 50;
    const km = Math.min(COMMERCE_DELIVERY_RADIUS_KM_MAX, Math.max(COMMERCE_DELIVERY_RADIUS_KM_MIN, kmRaw));
    formData.append('deliveryRadiusKm', String(km));
  }
  if (
    payload.deliveryScope === 'LOCAL' &&
    payload.latitude != null &&
    payload.longitude != null &&
    Number.isFinite(payload.latitude) &&
    Number.isFinite(payload.longitude)
  ) {
    formData.append('latitude', String(payload.latitude));
    formData.append('longitude', String(payload.longitude));
  }
  if (payload.deliveryScope === 'LISTED_ZONES') {
    if (
      Array.isArray(payload.listedZonesByJurisdiction) &&
      payload.listedZonesByJurisdiction.length > 0
    ) {
      formData.append('listedZonesByJurisdiction', JSON.stringify(payload.listedZonesByJurisdiction));
    } else if (Array.isArray(payload.listedGeorefZonaIds)) {
      const ids = payload.listedGeorefZonaIds.filter((x) => typeof x === 'string' && x.length > 0);
      if (ids.length > 0) {
        formData.append('listedGeorefZonaIds', JSON.stringify(ids));
      }
    }
  }
  return formData;
}

export async function createCommerce(payload: CreateCommercePayload) {
  const token = localStorage.getItem('access-Token-Edify');
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.post('/commerces/create', buildCommerceMultipart(payload), config);
}

export async function patchCommerce(commerceId: number, payload: CreateCommercePayload) {
  const token = localStorage.getItem('access-Token-Edify');
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.patch(`/commerces/${commerceId}`, buildCommerceMultipart(payload), config);
}

export async function deleteCommerce(commerceId: number) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.delete(`/commerces/${commerceId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function listedIdsFromGeo(geo: Record<string, unknown>): string[] | null {
  const raw = geo.listedGeorefZonaIds;
  if (!Array.isArray(raw)) return null;
  const ids = raw.filter((x): x is string => typeof x === 'string' && x.length > 0);
  return ids.length ? ids : null;
}

function pushGeorefZonaId(acc: string[], rawId: unknown) {
  if (typeof rawId === 'string' && rawId.trim()) acc.push(rawId.trim());
  else if (typeof rawId === 'number' && Number.isFinite(rawId)) acc.push(String(Math.trunc(rawId)));
}

function rowJurisdictionId(row: Record<string, unknown>): number | null {
  const flat = numOrNull(row.jurisdiccionId ?? row.jurisdiccion_id);
  if (flat != null) return flat;
  const nested = asRecord(row.jurisdiccion);
  return nested ? numOrNull(nested.id) : null;
}

function flatListedGeorefZonaIdsOnCommerce(d: Record<string, unknown>): string[] | null {
  const acc: string[] = [];
  for (const key of ['listedGeorefZonaIds', 'listed_georef_zona_ids'] as const) {
    const raw = d[key];
    if (!Array.isArray(raw)) continue;
    for (const x of raw) pushGeorefZonaId(acc, x);
  }
  return acc.length ? Array.from(new Set(acc)) : null;
}

function computeListedGeorefZonaIdsForDetail(
  d: Record<string, unknown>,
  primaryJurisdictionId: number | null
): string[] | null {
  const juris = d.jurisdicciones;
  const jurisArr = Array.isArray(juris) && juris.length > 0 ? juris : null;

  if (primaryJurisdictionId != null) {
    if (jurisArr) {
      const acc: string[] = [];
      for (const item of jurisArr) {
        const row = asRecord(item);
        if (!row) continue;
        const rowJ = rowJurisdictionId(row);
        if (rowJ == null || rowJ !== primaryJurisdictionId) continue;
        pushGeorefZonaId(acc, row.georefZonaId ?? row.georef_zona_id);
      }
      if (acc.length > 0) return Array.from(new Set(acc));
      return null;
    }
    return flatListedGeorefZonaIdsOnCommerce(d);
  }

  const acc: string[] = [];
  const fromFlat = flatListedGeorefZonaIdsOnCommerce(d);
  if (fromFlat) acc.push(...fromFlat);
  if (jurisArr) {
    for (const item of jurisArr) {
      const row = asRecord(item);
      if (!row) continue;
      pushGeorefZonaId(acc, row.georefZonaId ?? row.georef_zona_id);
    }
  }
  if (!acc.length) return null;
  return Array.from(new Set(acc));
}

function listedZoneLabelsFromCommerceJurisdicciones(
  d: Record<string, unknown>,
  primaryJurisdictionId: number | null
): Record<string, string> {
  const labels: Record<string, string> = {};
  const juris = d.jurisdicciones;
  if (!Array.isArray(juris) || primaryJurisdictionId == null) return labels;
  for (const item of juris) {
    const row = asRecord(item);
    if (!row) continue;
    const rowJ = rowJurisdictionId(row);
    if (rowJ == null || rowJ !== primaryJurisdictionId) continue;
    const rawId = row.georefZonaId ?? row.georef_zona_id;
    let id: string | null = null;
    if (typeof rawId === 'string' && rawId.trim()) id = rawId.trim();
    else if (typeof rawId === 'number' && Number.isFinite(rawId)) id = String(Math.trunc(rawId));
    if (!id) continue;
    const snap =
      typeof row.snapshotName === 'string'
        ? row.snapshotName.trim()
        : typeof row.snapshot_name === 'string'
          ? row.snapshot_name.trim()
          : '';
    if (snap) labels[id] = snap;
  }
  return labels;
}

export function extractDeliveryGeoFromCommerceCreateResponse(
  res: { data?: unknown }
): CommerceDeliveryGeo | null {
  const raw = res.data;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const g = o.deliveryGeo;
  if (!g || typeof g !== 'object') return null;
  const geo = g as Record<string, unknown>;
  const scope = geo.deliveryScope;
  if (scope !== 'LOCAL' && scope !== 'NATIONWIDE' && scope !== 'LISTED_ZONES') return null;
  const snap = geo.geocodeQuerySnapshot;
  if (typeof snap !== 'string') return null;
  const pid = geo.geocodePlaceId;
  const geocodePlaceId =
    pid === null || pid === undefined ? null : typeof pid === 'string' ? pid : null;
  return {
    deliveryScope: scope,
    latitude: numOrNull(geo.latitude),
    longitude: numOrNull(geo.longitude),
    deliveryRadiusKm: numOrNull(geo.deliveryRadiusKm),
    geocodeQuerySnapshot: snap,
    geocodePlaceId,
    listedGeorefZonaIds: listedIdsFromGeo(geo),
  };
}

export async function getCommerceJurisdictionDepartments(jurisdiccionId: number) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.get<{ message: string; data: { cities: CommerceDepartmentCity[] } }>(
    `/commerces/jurisdiccion/${jurisdiccionId}/departments`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}

export async function getCommerceMaterials(commerceId: number) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.get(`/commerces/materials/${commerceId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export type CommerceCatalogMaterialsPage = {
  items: unknown[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export function unwrapCommerceCatalogMaterialsPage(resBody: unknown): CommerceCatalogMaterialsPage | null {
  if (resBody == null || typeof resBody !== 'object' || Array.isArray(resBody)) return null;
  const o = resBody as Record<string, unknown>;
  const d = o.data;
  if (d == null || typeof d !== 'object' || Array.isArray(d)) return null;
  const inner = d as Record<string, unknown>;
  const items = Array.isArray(inner.items) ? inner.items : [];
  const page = Number(inner.page);
  const pageSize = Number(inner.pageSize);
  const total = Number(inner.total);
  const totalPages = Number(inner.totalPages);
  return {
    items,
    page: Number.isFinite(page) ? page : 1,
    pageSize: Number.isFinite(pageSize) ? pageSize : items.length,
    total: Number.isFinite(total) ? total : items.length,
    totalPages: Number.isFinite(totalPages) ? totalPages : 1,
  };
}

export async function getCommerceCatalogMaterials(
  commerceId: number,
  params?: { page?: number; pageSize?: number; q?: string }
) {
  const token = localStorage.getItem('access-Token-Edify');
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;
  const q = params?.q != null && params.q.length > 0 ? params.q : undefined;
  return axios.get(`/material-commerce/commerce/${commerceId}/catalog-materials`, {
    params: { page, pageSize, ...(q !== undefined ? { q } : {}) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export interface PatchCommerceMaterialItemPayload {
  price?: number;
  unquoted?: boolean;
  promotion?: string | null;
  promotionDiscount?: number | null;
  valueAddedTax?: boolean;
}

function materialCommerceItemPatchBody(payload: PatchCommerceMaterialItemPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (payload.price !== undefined) body.price = payload.price;
  if (payload.unquoted !== undefined) body.unquoted = payload.unquoted;
  if (payload.promotion !== undefined) body.promotion = payload.promotion;
  if (payload.promotionDiscount !== undefined) body.promotion_discount = payload.promotionDiscount;
  if (payload.valueAddedTax !== undefined) body.value_added_tax = payload.valueAddedTax;
  return body;
}

export async function patchCommerceMaterialItem(
  commerceId: number,
  itemId: number,
  payload: PatchCommerceMaterialItemPayload
) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.patch(
    `/material-commerce/commerce/${commerceId}/items/${itemId}`,
    materialCommerceItemPatchBody(payload),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}

export async function deleteCommerceMaterialItem(commerceId: number, itemId: number) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.delete(`/material-commerce/commerce/${commerceId}/items/${itemId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function postMaterialCommerceAdminCreate(body: Record<string, unknown>) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.post('/material-commerce/admin/create', body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

export async function getCommerce(commerceId: number) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.get(`/commerces/${commerceId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getCommerceListedZones(commerceId: number) {
  const token = localStorage.getItem('access-Token-Edify');
  return axios.get(`/commerces/${commerceId}/listed-zones`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export interface CommerceFormEditSeed {
  delivery: boolean;
  delivery_condition: string;
  deliveryScope: CommerceDeliveryScope;
  deliveryRadiusKm: number;
  mapLat: number | null;
  mapLng: number | null;
  listedZones: { ids: string[]; labels: Record<string, string> };
  listedZonesByJurisdiction: ListedZonesByJurisdictionBucketDto[] | null;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function stringField(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string') return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function deliveryGeoFromCommerceRecord(
  d: Record<string, unknown>,
  primaryJurisdictionId: number | null
): CommerceDeliveryGeo | null {
  const flatScope = d.delivery_scope;
  if (flatScope === 'LOCAL' || flatScope === 'NATIONWIDE' || flatScope === 'LISTED_ZONES') {
    return {
      deliveryScope: flatScope,
      latitude: numOrNull(d.latitude),
      longitude: numOrNull(d.longitude),
      deliveryRadiusKm: numOrNull(d.delivery_radius_km),
      geocodeQuerySnapshot:
        typeof d.geocode_query_snapshot === 'string' ? d.geocode_query_snapshot : '',
      geocodePlaceId:
        d.geocode_place_id === null || d.geocode_place_id === undefined
          ? null
          : typeof d.geocode_place_id === 'string'
            ? d.geocode_place_id
            : null,
      listedGeorefZonaIds: (() => {
        const hasJurisList = Array.isArray(d.jurisdicciones) && d.jurisdicciones.length > 0;
        const computed = computeListedGeorefZonaIdsForDetail(d, primaryJurisdictionId);
        if (computed != null) return computed;
        if (primaryJurisdictionId != null && hasJurisList) return null;
        return listedIdsFromGeo(d);
      })(),
    };
  }
  return null;
}

function parseListedZonesFromResponse(res: { data?: unknown } | null | undefined): {
  ids: string[];
  labels: Record<string, string>;
} {
  if (!res) return { ids: [], labels: {} };
  const root = asRecord(res.data);
  if (!root) return { ids: [], labels: {} };
  const inner = asRecord(root.data) ?? root;
  const rawIds = inner.georef_zona_ids;
  const idsFromTop = Array.isArray(rawIds)
    ? rawIds.filter((x): x is string => typeof x === 'string' && x.length > 0)
    : [];
  const items = Array.isArray(inner.items) ? inner.items : [];
  const labels: Record<string, string> = {};
  const idSet = new Set<string>(idsFromTop);
  for (const it of items) {
    const row = asRecord(it);
    if (!row) continue;
    const id = typeof row.georef_zona_id === 'string' ? row.georef_zona_id : null;
    if (!id) continue;
    idSet.add(id);
    const name = typeof row.nombre_snapshot === 'string' ? row.nombre_snapshot : '';
    if (name) labels[id] = name;
  }
  return { ids: Array.from(idSet), labels };
}

function parseListedZonesByJurisdictionFromDetail(
  d: Record<string, unknown>
): ListedZonesByJurisdictionBucketDto[] | null {
  const raw = d.listedZonesByJurisdiction ?? d.listed_zones_by_jurisdiction;
  if (!Array.isArray(raw)) return null;
  const out: ListedZonesByJurisdictionBucketDto[] = [];
  for (const el of raw) {
    const row = asRecord(el);
    if (!row) continue;
    const jid = numOrNull(row.jurisdiccionId ?? row.jurisdiccion_id);
    if (jid == null) continue;
    const idsRaw = row.georefZonaIds ?? row.georef_zona_ids;
    if (!Array.isArray(idsRaw)) continue;
    const georefZonaIds = idsRaw
      .filter((x): x is string => typeof x === 'string' && x.length > 0)
      .map((x) => x.trim());
    if (georefZonaIds.length > 0) out.push({ jurisdiccionId: jid, georefZonaIds });
  }
  return out.length > 0 ? out : null;
}

function listedZoneLabelsFromAllCommerceJurisdicciones(d: Record<string, unknown>): Record<string, string> {
  const labels: Record<string, string> = {};
  const juris = d.jurisdicciones;
  if (!Array.isArray(juris)) return labels;
  for (const item of juris) {
    const row = asRecord(item);
    if (!row) continue;
    const rawId = row.georefZonaId ?? row.georef_zona_id;
    let id: string | null = null;
    if (typeof rawId === 'string' && rawId.trim()) id = rawId.trim();
    else if (typeof rawId === 'number' && Number.isFinite(rawId)) id = String(Math.trunc(rawId));
    if (!id) continue;
    const snap =
      typeof row.snapshotName === 'string'
        ? row.snapshotName.trim()
        : typeof row.snapshot_name === 'string'
          ? row.snapshot_name.trim()
          : '';
    if (snap) labels[id] = snap;
  }
  return labels;
}

function commerceLogoUrlFromDetail(d: Record<string, unknown>): string {
  const v = d.logo ?? d.logoUrl ?? d.logo_url ?? d.image_profile_commerce ?? d.commerceLogo;
  if (typeof v !== 'string') return '';
  return v.trim();
}

export function parseCommerceDetailForEdit(
  getRes: { data?: unknown },
  listedZonesRes?: { data?: unknown } | null
): { prefill: CommerceFormPrefill; editSeed: CommerceFormEditSeed } | null {
  const root = asRecord(getRes.data);
  if (!root) return null;
  const d = asRecord(root.data) ?? root;
  const name = stringField(d, 'name').trim();
  if (!name) return null;
  const description = stringField(d, 'description').trim();
  const zone = stringField(d, 'zone').trim();
  const url_site_web = stringField(d, 'url_site_web').trim();
  let jurisdiccionId: number | '' | null = '';
  const jObj = asRecord(d.jurisdiccion);
  const jid = jObj ? numOrNull(jObj.id) : null;
  if (jid != null) jurisdiccionId = jid;
  const primaryJurisdictionId = jid;
  const delRaw = d.delivery;
  const delivery =
    delRaw === true ||
    delRaw === 1 ||
    (typeof delRaw === 'string' && (delRaw === 'true' || delRaw === '1'));
  const delivery_condition = stringField(d, 'delivery_condition').trim();
  const geo = deliveryGeoFromCommerceRecord(d, primaryJurisdictionId);
  const deliveryScope: CommerceDeliveryScope = geo?.deliveryScope ?? 'LOCAL';
  const kmRaw =
    deliveryScope === 'LOCAL' && geo?.deliveryRadiusKm != null && Number.isFinite(geo.deliveryRadiusKm)
      ? geo.deliveryRadiusKm
      : 50;
  const deliveryRadiusKm = Math.min(
    COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
    Math.max(COMMERCE_DELIVERY_RADIUS_KM_MIN, Math.round(kmRaw))
  );
  const mapLat = deliveryScope === 'LOCAL' ? geo?.latitude ?? null : null;
  const mapLng = deliveryScope === 'LOCAL' ? geo?.longitude ?? null : null;
  const apiBuckets =
    deliveryScope === 'LISTED_ZONES' ? parseListedZonesByJurisdictionFromDetail(d) : null;
  const geoListed = geo?.listedGeorefZonaIds;
  let listedIds: string[] = [];
  let listedZonesByJurisdiction: ListedZonesByJurisdictionBucketDto[] | null = null;
  const parsedListed = parseListedZonesFromResponse(listedZonesRes ?? null);
  if (deliveryScope === 'LISTED_ZONES' && apiBuckets && apiBuckets.length > 0) {
    listedZonesByJurisdiction = apiBuckets;
    listedIds = apiBuckets.flatMap((b) => b.georefZonaIds);
  } else {
    listedZonesByJurisdiction = null;
    listedIds =
      deliveryScope === 'LISTED_ZONES' && Array.isArray(geoListed)
        ? geoListed.filter((x): x is string => typeof x === 'string' && x.length > 0)
        : [];
    if (deliveryScope === 'LISTED_ZONES' && listedIds.length === 0 && parsedListed.ids.length > 0) {
      listedIds = [...parsedListed.ids];
    }
  }
  const labelsFromJurisPrimary = listedZoneLabelsFromCommerceJurisdicciones(d, primaryJurisdictionId);
  const labelsFromJurisAll = listedZoneLabelsFromAllCommerceJurisdicciones(d);
  const labels: Record<string, string> = {
    ...(listedZonesByJurisdiction ? labelsFromJurisAll : labelsFromJurisPrimary),
    ...parsedListed.labels,
  };
  for (const id of listedIds) {
    if (labels[id] == null && parsedListed.labels[id]) labels[id] = parsedListed.labels[id];
  }
  const logoUrl = commerceLogoUrlFromDetail(d);
  const prefill: CommerceFormPrefill = {
    name,
    description,
    url_site_web,
    zone,
    placeInput: zone,
    jurisdiccionId,
    ...(logoUrl ? { logoUrl } : {}),
  };
  const editSeed: CommerceFormEditSeed = {
    delivery,
    delivery_condition,
    deliveryScope,
    deliveryRadiusKm,
    mapLat,
    mapLng,
    listedZones: { ids: listedIds, labels },
    listedZonesByJurisdiction,
  };
  return { prefill, editSeed };
}
